@if (!inline()) {
<div class="zag-date-range-picker__trigger-wrapper" cdkOverlayOrigin #origin="cdkOverlayOrigin">
  <div
    class="zag-date-range-picker__inputs"
    [class.zag-date-range-picker__inputs--recurring]="recurring()"
    [class.zag-date-range-picker__inputs--readonly]="readonly()"
  >
    <!-- Start input -->
    <input
      [zagScrollIntoView]="true"
      type="text"
      class="zag-date-range-picker__input"
      [class.zag-date-range-picker__input--error]="startInputError()"
      [attr.placeholder]="maskInput() ? maskPlaceholder() : startPlaceholder()"
      [attr.aria-label]="startPlaceholder()"
      [attr.aria-invalid]="startInputError()"
      [attr.aria-required]="required()"
      [disabled]="disabled()"
      [readonly]="readonly()"
      [value]="startInputText()"
      (input)="onStartInputChange($event)"
      (blur)="onStartInputBlur()"
      (focus)="onStartInputFocus()"
      (keydown)="onStartInputKeyDown($event)"
    />

    <span class="zag-date-range-picker__separator" aria-hidden="true">{{ valueSeparator() }}</span>

    <!-- End input -->
    <input
      [zagScrollIntoView]="true"
      type="text"
      class="zag-date-range-picker__input"
      [class.zag-date-range-picker__input--error]="endInputError()"
      [attr.placeholder]="maskInput() ? maskPlaceholder() : endPlaceholder()"
      [attr.aria-label]="endPlaceholder()"
      [attr.aria-invalid]="endInputError()"
      [attr.aria-required]="required()"
      [disabled]="disabled()"
      [readonly]="readonly()"
      [value]="endInputText()"
      (input)="onEndInputChange($event)"
      (blur)="onEndInputBlur()"
      (focus)="onEndInputFocus()"
      (keydown)="onEndInputKeyDown($event)"
    />

    @if (showClearButton()) {
    <button type="button" class="zag-date-range-picker__clear" aria-label="Clear date range" (click)="clear($event)">
      <span zagIcon="close"></span>
    </button>
    }
  </div>
</div>

<ng-template
  cdkConnectedOverlay
  [cdkConnectedOverlayOpen]="open()"
  [cdkConnectedOverlayOrigin]="origin"
  [cdkConnectedOverlayHasBackdrop]="true"
  [cdkConnectedOverlayBackdropClass]="isMobile() ? 'zag-picker-dialog-backdrop' : 'zag-date-range-picker-overlay-backdrop'"
  [cdkConnectedOverlayPanelClass]="isMobile() ? 'zag-picker-dialog-panel' : ''"
  [cdkConnectedOverlayPositions]="isMobile() ? mobileOverlayPositions : overlayPositions"
  (backdropClick)="isMobile() ? closeMobile() : close()"
  (detach)="isMobile() ? closeMobile() : close()"
>
  <ng-container *ngTemplateOutlet="panelBody"></ng-container>
</ng-template>
}

<ng-template #panelBody>
  <div
    class="zag-date-range-picker-panel"
    [class.zag-date-range-picker-panel--inline]="inline()"
    role="dialog"
    aria-modal="true"
    aria-label="Date range picker"
  >
    @if (isMobile() && !inline()) {
    <div class="zag-picker-mobile-header">
      @if (fieldLabel()) {
      <span class="zag-picker-mobile-header__label">{{ fieldLabel() }}</span>
      }
      <button type="button" class="zag-picker-mobile-header__close" [attr.aria-label]="'picker.close' | translate" (click)="closeMobile()">
        <span zagIcon="close"></span>
      </button>
    </div>
    }
    <div class="zag-date-range-picker-panel__header">
      <button type="button" class="zag-date-range-picker-panel__nav" aria-label="Previous" (click)="navigatePrevious()">
        <span zagIcon="chevron_left"></span>
      </button>

      <button type="button" class="zag-date-range-picker-panel__title" [disabled]="viewMode() === 'decade'" (click)="zoomOut()">
        {{ headerLabel() }}
      </button>

      <button type="button" class="zag-date-range-picker-panel__nav" aria-label="Next" (click)="navigateNext()">
        <span zagIcon="chevron_right"></span>
      </button>
    </div>

    @if (showSelectionHint()) {
    <div class="zag-date-range-picker-panel__hint">Picking: <strong>{{ selectionHint() }}</strong></div>
    } @if (viewMode() === 'day') {
    <div class="zag-date-range-picker-panel__weekdays">
      @for (weekday of weekdayLabels(); track weekday) {
      <span class="zag-date-range-picker-panel__weekday">{{ weekday }}</span>
      }
    </div>

    <div class="zag-date-range-picker-panel__grid zag-date-range-picker-panel__grid--days" (mouseleave)="clearPreviewDate()">
      @for (cell of dayCells(); track cell.date.toISOString()) {
      <button
        type="button"
        class="zag-date-range-picker-panel__cell"
        [class.zag-date-range-picker-panel__cell--muted]="!cell.isCurrentMonth"
        [class.zag-date-range-picker-panel__cell--today]="cell.isToday"
        [class.zag-date-range-picker-panel__cell--range]="cell.isInRange"
        [class.zag-date-range-picker-panel__cell--range-start]="cell.isRangeStart"
        [class.zag-date-range-picker-panel__cell--range-end]="cell.isRangeEnd"
        [class.zag-date-range-picker-panel__cell--preview-range]="cell.isInPreviewRange"
        [class.zag-date-range-picker-panel__cell--preview-start]="cell.isPreviewStart"
        [class.zag-date-range-picker-panel__cell--preview-end]="cell.isPreviewEnd"
        [disabled]="cell.isDisabled"
        (mouseenter)="setPreviewDate(cell)"
        (focus)="setPreviewDate(cell)"
        (click)="selectDay(cell)"
      >
        {{ cell.dayOfMonth }}
      </button>
      }
    </div>
    } @if (viewMode() === 'month') {
    <div class="zag-date-range-picker-panel__grid zag-date-range-picker-panel__grid--months">
      @for (cell of monthCells(); track cell.value) {
      <button
        type="button"
        class="zag-date-range-picker-panel__cell zag-date-range-picker-panel__cell--option"
        [class.zag-date-range-picker-panel__cell--selected]="cell.isSelected"
        [class.zag-date-range-picker-panel__cell--today]="cell.isCurrent"
        [disabled]="cell.isDisabled"
        (click)="selectMonth(cell)"
      >
        {{ cell.label }}
      </button>
      }
    </div>
    } @if (viewMode() === 'year') {
    <div class="zag-date-range-picker-panel__grid zag-date-range-picker-panel__grid--years">
      @for (cell of yearCells(); track cell.value) {
      <button
        type="button"
        class="zag-date-range-picker-panel__cell zag-date-range-picker-panel__cell--option"
        [class.zag-date-range-picker-panel__cell--outside]="cell.isOutsideRange"
        [class.zag-date-range-picker-panel__cell--selected]="cell.isSelected"
        [class.zag-date-range-picker-panel__cell--today]="cell.isCurrent"
        [disabled]="cell.isDisabled"
        (click)="selectYear(cell)"
      >
        {{ cell.label }}
      </button>
      }
    </div>
    } @if (viewMode() === 'decade') {
    <div class="zag-date-range-picker-panel__grid zag-date-range-picker-panel__grid--decades">
      @for (cell of decadeCells(); track cell.value) {
      <button
        type="button"
        class="zag-date-range-picker-panel__cell zag-date-range-picker-panel__cell--option"
        [class.zag-date-range-picker-panel__cell--outside]="cell.isOutsideRange"
        [class.zag-date-range-picker-panel__cell--selected]="cell.isSelected"
        [class.zag-date-range-picker-panel__cell--today]="cell.isCurrent"
        [disabled]="cell.isDisabled"
        (click)="selectDecade(cell)"
      >
        {{ cell.label }}
      </button>
      }
    </div>
    } @if (isMobile() && !inline()) {
    <div class="zag-picker-mobile-footer">
      <button type="button" class="zag-picker-mobile-footer__apply" [disabled]="!hasPendingRange()" (click)="applyMobile()">
        {{ 'picker.apply' | translate }}
      </button>
    </div>
    }
  </div>
</ng-template>

@if (inline()) {
<ng-container *ngTemplateOutlet="panelBody"></ng-container>
}
